import { FC, useCallback, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Box, Typography } from '@mui/material';
// Components
const Button = dynamic(() => import('~/components/Button'), {
  ssr: false,
});
import CarTopLabel from '~/components/CarTopLabel';

import CarImagesKitCarousel from '~/components/CarImagesKitCarousel/';
import { COLORS } from '~/data/ui/colors';

// Data
import RESOURCES from '~/resources';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';
import { FamilyCarPromoCardProps } from '~/data/types/car/cars';
import { imageKitThumb } from '~/data/imageKitData';

// Utils
import { generateSeed, getRandomIndices } from '~/utils/getRandomIndices';
import { sortLabelsByRank } from '~/utils/cleanObject';
import YellowClockIcon from '~/assets/icons/jsx/yellowClock';

// Hooks
import { useImagesKitS3 } from '~/hooks/useImageKitS3';
import { EActionEvent, ECategoryEvent, useEvents } from '~/hooks/useEvents';
import { useContactUsPopup } from '~/hooks/useContactUsPopup';

// Store
// Styles
import useStyles, {
  bottomContainer,
  brandNameStyle,
  contactUsButton,
  contactUsButtonText,
  contentContainer,
  modelNameStyle,
  priceStyle,
  topLabelStyles,
  yellowLineContainerStyles,
  yellowLineStyles,
  yellowLineLeaseContainerStyles,
  StyledBlueLine,
  StyledBottomLabelBox,
  StyledFamilyCarPromoBox,
} from './useStyles';
import { CarImagesCarouselStyle } from '../UsedCarCard/useStyles';
// Icons
import { useAppDispatch } from '~/redux-toolkit/store/hooks';
import { CONTACT_US_REDIRECTION } from '~/data/consts';
import { ESourceEventOptions } from '~/data/lead/enums';
import { EContactUsFlow } from '~/data/ContactUsPopup/enums';
import { useIsLeaseCar } from '~/hooks/useIsLeaseCar';
import YellowLineTitle from '~/assets/icons/jsx/yellowLineTitle';
import { useCarsPagesLeadLocation } from '~/hooks/useCarsPagesLeadLocation';
import { useLeadEventDetails } from '~/hooks/useLeadEventDetails';
import { priceStyles } from '../LobbyBudget/useStyles';
import Frame from '~/components/Frame/frame';
import FamilyCarPromoIcon from '~/assets/icons/jsx/familyCarsPromoIcon';
import { getPromotionCampaignCards } from '~/redux-toolkit/thunks/resultPageThunks';
import { setFamilyCarPromoCard } from '~/redux-toolkit/slices/resultPageSlice';

const FamilyCarPromoCard: FC<FamilyCarPromoCardProps> = ({
  familyCarPromo,
}) => {
  const events = useEvents();
  const dispatch = useAppDispatch();

  const isPrivateLeasing = useIsLeaseCar();

  const { leadLocationOption } = useCarsPagesLeadLocation();

  useEffect(() => {
    dispatch(getPromotionCampaignCards());
  }, [dispatch]);

  const images: (string | null)[] = useImagesKitS3(
    familyCarPromo?.images || [],
  );

  const topLabel = useMemo(() => {
    if (familyCarPromo?.carLabels && familyCarPromo.carLabels.data.length > 0) {
      const sortedLabels = sortLabelsByRank(familyCarPromo.carLabels.data);
      return sortedLabels[0].title ?? '';
    }

    if (familyCarPromo?.carLabels && familyCarPromo.carLabels.data.length > 0) {
      const seed = generateSeed(`${familyCarPromo.id}`, new Date());
      const randomIndices = getRandomIndices(
        familyCarPromo.carLabels.data.length,
        1,
        seed,
      );
      return familyCarPromo.carLabels.data[randomIndices[0]].title;
    }

    return '';
  }, [familyCarPromo]);

  const { openContactUsModal } = useContactUsPopup();

  const classes = useStyles();

  const leadEventDetails = useLeadEventDetails(
    leadLocationOption,
    // familyCarPromo,
  );
  const onClickLeaveDetails = useCallback(() => {
    events.sendEvent(
      {
        action: EActionEvent.CAR_PAGE_LEAD,
        category: ECategoryEvent.LEAD,
      },
      {
        ...leadEventDetails,
        carModel: familyCarPromo?.modelName,
        carMake: familyCarPromo?.manufacturerName,
        dealType: 'familyCarPromo',
      },
    );
    dispatch(setFamilyCarPromoCard(familyCarPromo));

    openContactUsModal({
      source: isPrivateLeasing
        ? ESourceEventOptions.LEASE_CAR_RESULT_PAGE
        : ESourceEventOptions.NEW_CAR_RESULT_PAGE,
      flow: EContactUsFlow.LEAVE_DETAILS,
      redirectTo: CONTACT_US_REDIRECTION.RESULTS_PAGE,
      campaignNumber: familyCarPromo.leadDetails.campaignNumber,
      subMedia: familyCarPromo.leadDetails.subMedia,
      lineOfBusiness: familyCarPromo.leadDetails.lineOfBusiness,
      note: `${familyCarPromo.manufacturerName} | ${familyCarPromo.modelName}, ${familyCarPromo.leadDetails.note}`,
    });
  }, [
    events,
    leadEventDetails,
    openContactUsModal,
    isPrivateLeasing,
    familyCarPromo,
    dispatch,
  ]);

  return (
    <Box sx={classes.root} tabIndex={0}>
      <Box sx={classes.cardContainer}>
        <Box sx={classes.sliderContainer}>
          <CarImagesKitCarousel
            ikThumb={imageKitThumb.carCard}
            images={images}
            customStyle={CarImagesCarouselStyle}
          />
          {topLabel && (
            <Box sx={classes.bottomLabel}>
              <CarTopLabel title={topLabel} customTextStyle={topLabelStyles} />
            </Box>
          )}
        </Box>

        <Box sx={bottomContainer}>
          {/* CONTENT */}
          <Box sx={contentContainer}>
            <Typography
              tabIndex={0}
              variant="h3"
              sx={{ ...brandNameStyle, ...classes.carTitle }}
            >
              {familyCarPromo?.manufacturerName}

              <Box component={'span'} sx={modelNameStyle}>
                {familyCarPromo?.modelName}
              </Box>
            </Typography>
          </Box>

          <StyledBottomLabelBox>
            <Frame
              text={familyCarPromo?.bottomLabelTitle}
              icon={<YellowClockIcon />}
              color={`${COLORS.yellow.light1}`}
            />
          </StyledBottomLabelBox>
        </Box>

        {/* PRICE */}
        <Box sx={priceStyles}>
          <Box sx={classes.priceWrapper}>
            <Typography sx={priceStyle}>
              {familyCarPromo?.promoTitle ||
                RESOURCES.FAMILY_CAR_PROMO_BOTTOM_TITLE}

              <Box
                component="span"
                sx={{
                  ...yellowLineContainerStyles,
                  ...(isPrivateLeasing || true
                    ? yellowLineLeaseContainerStyles
                    : null),
                }}
              >
                <YellowLineTitle style={yellowLineStyles} />
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* BUTTONS */}
        <Box sx={classes.btnsDivStyle}>
          <Button
            label={RESOURCES.CAR_FLOW_SUMMARY_MORE_DETAILS}
            type={BUTTON_TYPES.MAIN}
            size={BUTTON_SIZES.LARGE}
            customStyle={contactUsButton}
            typographyStyle={contactUsButtonText}
            onClickFunc={onClickLeaveDetails}
          />
        </Box>
      </Box>
      <StyledFamilyCarPromoBox>
        <FamilyCarPromoIcon Component={StyledBlueLine} />
      </StyledFamilyCarPromoBox>
    </Box>
  );
};

export default FamilyCarPromoCard;
